import axios from "axios";
import { axiosInstance } from "../config/configApi";
import { apiEndPoint, fastLoginSessionKillUrl } from "../../statics/config";
import { getUserLanguage } from "../user/user";
import moment from "moment";
import { readCache, createRequestQueue, getReadScope } from "../../utils/readCache";
import { loadRequest } from "../../utils/loadRequest";

import { validateChannelPlayback } from "./channelPlayback";

const queuePrograms = createRequestQueue(4);
const validList = response => {
  if (!Array.isArray(response?.data)) throw new Error("Invalid broadcast list");
  return response;
};

const getChannels = () => readCache.read("channels", async () =>
  validList(await loadRequest(options => axios.get("/client/channel", options))), 30000
).then(response => ({ ...response, data: JSON.parse(JSON.stringify(response.data)) }));

const getCurrentProgramById = async (channelId) => {
  return await axios.get("/client/channel/" + channelId + "/program/current");
};

const getProgramsById = (channelId, selectedDate) => {
  const day = moment(selectedDate, ["DD.MM.YYYY", "YYYY-MM-DD", "YYYYMMDD", moment.ISO_8601], true);
  if (!day.isValid()) return Promise.reject(new Error("EPG tarihi geçersiz."));
  const date = day.format("DD.MM.YYYY");
  const scope = getReadScope();
  return readCache.read(`programs:${channelId}:${date}`, () => queuePrograms(async () => {
    if (scope !== getReadScope()) throw new Error("Read context changed");
    return validList(await loadRequest(options => axiosInstance.get(
      "/client/channel/" + channelId + "/program", { ...options, params: { date } }
    )));
  }), 120000);
};

const toggleFavById = async (channelId) => {
  const response = await axios.post("/client/interact/fav/" + channelId);
  readCache.clear();
  // Genesis returns a boolean; legacy clients consume an interaction array.
  // Callers update only the favorite flag, preserving the channel's lock state.
  if (typeof response.data === "boolean") {
    return { ...response, data: response.data ? ["CHANNEL_FAV"] : [] };
  }
  if (!Array.isArray(response.data)) throw new Error("Invalid channel favorite response");
  return response;
};

const toggleLockById = async (channelId, pin) => {
  const response = await axios.post(
    "/client/interact/lock/" + channelId,
    pin,
    {
      headers: {
        "Content-Type": "text/plain"
      }
    }
  );
  readCache.clear(); // Lock state changes availability and every cached channel projection.
  return response;
};

const isChannelLocked = (channel) => {
  return (
    channel?.interactions?.includes("CHANNEL_LOCK") ||
    channel?.islocked === "1" ||
    channel?.isLocked === true
  );
};
const removeAllLockedChannels = async (pin = "", columnCode = null, profileId) => {
  const activeProfile = JSON.parse(localStorage.getItem("profile") || "{}");
  if (profileId && String(profileId) !== String(activeProfile.id)) {
    throw new Error("Select this profile before removing its channel locks.");
  }
  readCache.clear();
  let channels = [];
  const allChannelsResponse = await getChannels();
  if (Array.isArray(allChannelsResponse?.data)) {
    channels = allChannelsResponse.data;
  }
  if (!channels.some(isChannelLocked) && columnCode) {
    try {
      const lockedListResponse = await getLockedChannels(columnCode);
      let lockedList = [];
      if (Array.isArray(lockedListResponse?.data)) {
        lockedList = lockedListResponse.data;
      } else if (Array.isArray(lockedListResponse?.data?.data)) {
        lockedList = lockedListResponse.data.data;
      }
      const merged = [...channels, ...lockedList];
      const map = new Map();

      merged.forEach((item) => {
        const key = item?.id ?? item?.channelid ?? item?.channelcode;
        if (key != null) {
          map.set(key, item);
        }
      });

      channels = Array.from(map.values());
    } catch (error) {
      console.error("[ltv] getLockedChannels fallback failed =>", error);
    }
  }
  const lockedChannels = channels.filter(isChannelLocked);
  if (!lockedChannels.length) {
    return {
      success: true,
      unlockedCount: 0,
      failedChannels: [],
      remainingLockedChannels: []
    };
  }
  const results = await Promise.all(
    lockedChannels.map(async (channel) => {
      const channelId = channel?.id ?? channel?.channelid;
      try {
        const response = await toggleLockById(channelId, pin);
        const responseData = response?.data;
        const stillLocked =
          Array.isArray(responseData) &&
          responseData.includes("CHANNEL_LOCK");
        return {
          channelId,
          success: !stillLocked,
          responseData
        };
      } catch (error) {
        console.error("[ltv] unlock request failed for channel =>", {
          channelId,
          error
        });
        return {
          channelId,
          success: false,
          error
        };
      }
    })
  );
  const failedChannels = results.filter((item) => !item.success);
  readCache.clear();
  const verifyResponse = await getChannels();
  const verifyChannels = Array.isArray(verifyResponse?.data)
    ? verifyResponse.data
    : [];
  const remainingLockedChannels = verifyChannels.filter(isChannelLocked);
  if (failedChannels.length || remainingLockedChannels.length) {
    throw new Error(
      `Some channel locks could not be removed. Failed: ${failedChannels.length}, Remaining: ${remainingLockedChannels.length}`
    );
  }
  return {
    success: true,
    unlockedCount: results.length,
    failedChannels: [],
    remainingLockedChannels: []
  };
};
const getChannelPlayUrl = async (channelId, startTime, requestOptions = {}) => {
  let url = "";
  if (startTime) {
    url = "/client/channel/" + channelId + "/url?startTime=" + startTime;
  } else {
    url = "/client/channel/" + channelId + "/url";
  }
  return validateChannelPlayback(await axios.get(url, {
    ...requestOptions,
    headers: { "Content-Type": "text/plain" }
  }));
};

export const getFiltersByLanguage = async (lang) => {
  return await axios.get(`/client/genre/${lang}`);
};

// getUserLanguage dil adının tamamını döndüğü için convert işlemi yapılıyor
const genreLanguageCodes = {
  Turkish: "tr",
  English: "eng",
  Arabic: "arb"
};

export const getChannelGenres = async () => {
  const { data } = await getUserLanguage();
  const filters = await getFiltersByLanguage(genreLanguageCodes[data?.language] || "eng");
  return filters?.data ?? [];
};

const getProgramPlayUrl = async (epgId, channelId, contentType, requestOptions = {}) => {
  return validateChannelPlayback(await axios.get("/client/channel/" + epgId + "/watch", requestOptions));
};

const getLtvChannels = async (columnCode, page, count) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");

  return await axios.get(`${apiEndpoint}/sdk_getchannellist.jsp`, {
    params: {
      columncode: columnCode,
      pageno: page,
      numperpage: count,
      ordertype: 1, // order by channel no
      isqueryfavorite: 1,
      isqueryprevue: 1,
      ischecklock: 1
    }
  });
};

const getChannel = async (channelCode, productcode) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");

  return await axios.get(`${apiEndpoint}/sdk_getchanneldetail.jsp`, {
    params: {
      channelcode: channelCode,
      ischecksubscribe: 1,
      productcode: productcode
    }
  });
};

const getLockedChannels = async (columnCode) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getchannellocklist.jsp`, {
    params: {
      columncode: columnCode,
      pageno: 1,
      numperpage: 500, // to fix
      ordertype: 1
    }
  });
};

const getFavoriteChannels = async (columnCode) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.post(
    `${apiEndpoint}/sdk_getfullfavoritelist.jsp`,
    `favoritetype=5&ordertype=2&isqueryprevue=1&pagesize=500&tvcolumncode=${columnCode}&isquerylock=1`
  );
};

const getUserMyTvChannel = async (beginTime, endTime, language) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getmytvchannel.jsp`, {
    params: {
      langtype: language,
      starttime: beginTime,
      endtime: endTime,
      pageno: 1,
      numberpage: 500
    }
  });
};

const getPrevueInfo = async (prevueCode) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getprevueinfo.jsp`, {
    params: {
      prevuecodes: prevueCode
    }
  });
};

const getLivePlayUrl = async (
  channelCode,
  columnCode,
  playtype,
  productCode
) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");

  return await axios.get(`${apiEndpoint}/sdk_getppvtvplayurl.jsp`, {
    params: {
      channelcode: channelCode,
      columncode: columnCode,
      urlredirect: 1,
      playtype: playtype,
      productcode: productCode
    }
  });
};

const checkPpvAuth = async (prevueCode) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getippvauth.jsp`, {
    params: {
      prevuecode: prevueCode
    }
  });
};

const getProgramListByChannel = async (channelId, beginTime, endTime) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getprevuellist.jsp`, {
    params: {
      channelcode: channelId,
      begintime: beginTime,
      endtime: endTime
    }
  });
};

const getCurrentPrevue = async (channelCode) => {
  let apiEndpoint = localStorage.getItem("interfaceurl");

  return await axios.get(`${apiEndpoint}/sdk_getcurrentprevue.jsp`, {
    params: {
      channelcodes: channelCode
    }
  });
};

const getAllChannelsAndPrograms = async (beginTime, endTime) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getinfobarprevue.jsp`, {
    params: {
      // channelcode: channelId,
      // begintime: beginTime,
      // endtime: endTime,
    }
  });
};

const getMultiProgramData = async (channelCodes, beginTime, endTime) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axiosInstance.get(`${apiEndPoint}/sdk_getmultiprevuedata.jsp`, {
    params: {
      begintime: beginTime,
      endtime: endTime,
      channelcodelist: channelCodes
    }
  });
};

const getTvSearchResult = async (term, userLevel, columnCodes) => {
  let apiEndpoint = localStorage.getItem("interfaceurl");
  return await axios.post(
    `${apiEndpoint}/getsearchdata.jsp`,
    `conditiontype=16|17|18|19|20|21|22&condition=${encodeURIComponent(
      encodeURIComponent(term)
    )}&contenttype=2&languagetype=tur|eng&ratingid=${userLevel}&pageno=1&numperpage=100&mediaservices=-1|2&columncode=${columnCodes}`
  );
};

const addLastWatchedChannel = async (dataKey, dataValue) => {
  let apiEndpoint = localStorage.getItem("interfaceurl");
  return await axios.post(
    `${apiEndpoint}/sdk_addrecentchannel.jsp`,
    `datakey=${dataKey}&datavalue=${encodeURIComponent(
      encodeURIComponent(dataValue)
    )}`
  );
};

const getUserLastWatchedChannels = async () => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  return await axios.get(`${apiEndpoint}/sdk_queryrecentchannel.jsp`);
};

const getLiveHotChannels = async (beginTime, endTime) => {
  // this is a "post" request in the document
  var apiEndpoint = localStorage.getItem("interfaceurl");
  return await axios.get(`${apiEndpoint}/sdk_gethotchannelprevue.jsp`, {
    params: {
      starttime: beginTime,
      endtime: endTime
    }
  });
};

export {
  getChannels,
  getCurrentProgramById,
  getProgramsById,
  toggleFavById,
  toggleLockById,
  removeAllLockedChannels,
  getChannelPlayUrl,
  getProgramPlayUrl,
  getLtvChannels,
  getChannel,
  getProgramListByChannel,
  getLockedChannels,
  getLivePlayUrl,
  getFavoriteChannels,
  getAllChannelsAndPrograms,
  getMultiProgramData,
  getPrevueInfo,
  getUserMyTvChannel,
  checkPpvAuth,
  getTvSearchResult,
  getCurrentPrevue,
  addLastWatchedChannel,
  getUserLastWatchedChannels,
  getLiveHotChannels
};
